import { useEffect, useState } from "react";
import { Search, Zap, Sparkles } from "lucide-react";
import { adminApi, STATUS, CHECKIN, Badge, fmtDate } from "./adminApi";
import { BookingDrawer } from "./BookingDrawer";

const FILTERS = [
  ["tutte", "Tutte"], ["nuove", "Da contattare"], ["fissate", "Visite fissate"],
  ["laser", "Sedute laser"], ["gbt", "Igiene GBT"], ["concluse", "Svolte"], ["attenzione", "Da richiamare"],
];

const Stat = ({ label, value, testid, accent }) => (
  <div className={`rounded-[1.5rem] p-5 ${accent ? "bg-[#1B3B28] text-[#FCFBF7]" : "bg-[#FCFBF7] border border-[#1B3B28]/10"}`} data-testid={testid}>
    <p className={`text-xs ${accent ? "text-[#86C89B]" : "text-[#526356]"}`}>{label}</p>
    <p className="f-serif text-5xl leading-none mt-3">{value ?? "–"}</p>
  </div>
);

const Row = ({ b, onOpen }) => (
  <button onClick={() => onOpen(b)} data-testid={`booking-row-${b.id}`}
    className="w-full text-left grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr_1fr_0.6fr] gap-3 items-center px-5 py-4 rounded-2xl bg-[#FCFBF7] border border-[#1B3B28]/8 hover:border-[#86C89B] hover:shadow-[0_10px_30px_-15px_rgba(27,59,40,0.3)] transition-[border-color,box-shadow]">
    <div className="col-span-2 md:col-span-1">
      <p className="f-serif text-2xl text-[#1B3B28] leading-tight">{b.nome} {b.cognome}</p>
      <p className="text-xs text-[#526356] mt-0.5">
        {b.trattamento === "igiene_gbt" ? <span className="inline-flex items-center gap-1 text-[#3E6B48] font-medium" data-testid={`booking-gbt-tag-${b.id}`}><Sparkles size={11} /> Igiene GBT</span> : "Visita parodontale"} · {b.telefono} · richiesta del {fmtDate(b.created_at)}
      </p>
    </div>
    <div><Badge cls={STATUS[b.status].cls}>{STATUS[b.status].label}</Badge></div>
    <div className="text-sm text-[#1B3B28]">
      {b.appointment_date ? `${fmtDate(b.appointment_date, { weekday: "short", day: "numeric", month: "short" })} · ${b.appointment_time}`
        : b.requested_date ? <span className="text-[#8A5A2B]" data-testid={`booking-requested-${b.id}`}>Richiesto: {fmtDate(b.requested_date, { weekday: "short", day: "numeric", month: "short" })} · {b.requested_time}</span> : "—"}
      {b.laser_session && <span className="ml-1.5 text-[#3E6B48]" title="Seduta laser"><Zap size={13} className="inline" /></span>}
    </div>
    <div>{b.checkin_response ? <Badge cls={CHECKIN[b.checkin_response].cls}>{CHECKIN[b.checkin_response].label}</Badge> : <span className="text-xs text-[#526356]">—</span>}</div>
    <div className="text-sm text-[#526356] md:text-right">Ansia {b.ansia}/10</div>
  </button>
);

export const BookingsTab = () => {
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("tutte");
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(null);

  useEffect(() => { adminApi.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => adminApi.get("/admin/bookings", { params: { q, filtro } }).then(({ data }) => setRows(data)), 250);
    return () => clearTimeout(t);
  }, [q, filtro]);

  return (
    <div data-testid="bookings-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Richieste totali" value={stats.totale} testid="stat-total" />
        <Stat label="Da contattare" value={stats.nuove} testid="stat-new" accent />
        <Stat label="Visite questa settimana" value={stats.settimana} testid="stat-week" />
        <Stat label="Da richiamare (check-in)" value={stats.attenzione} testid="stat-attention" />
      </div>
      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#526356]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, telefono o email" data-testid="bookings-search-input"
            className="w-full rounded-full border border-[#1B3B28]/15 bg-[#FCFBF7] pl-11 pr-4 py-3 text-sm text-[#1B3B28] focus:outline-none focus:ring-2 focus:ring-[#86C89B]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFiltro(k)} data-testid={`bookings-filter-${k}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${filtro === k ? "bg-[#1B3B28] text-[#FCFBF7]" : "bg-[#FCFBF7] text-[#1B3B28] border border-[#1B3B28]/10 hover:bg-[#E6ECE4]"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="hidden md:grid grid-cols-[1.6fr_1fr_1fr_1fr_0.6fr] gap-3 px-5 mt-8 mb-2 text-xs text-[#526356]">
        <span>Paziente</span><span>Stato</span><span>Visita</span><span>Come sta</span><span className="text-right">Ansia</span>
      </div>
      <div className="space-y-2 mt-4 md:mt-0" data-testid="bookings-list">
        {rows?.map((b) => <Row key={b.id} b={b} onOpen={setOpen} />)}
        {rows?.length === 0 && <p className="text-[#526356] py-10 text-center" data-testid="bookings-empty">Nessuna prenotazione trovata.</p>}
      </div>
      <BookingDrawer booking={open} onClose={() => setOpen(null)} />
    </div>
  );
};
